import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { getToken, useMsalAuth } from "../../authConfig";
import { getHeaders } from "../../api/api";
import { Button, Modal } from "../../components/ui";
import { choicePreviewSchema } from "./choiceModel";
import type { ChoiceEditorConfig, ChoicePreview, WorkflowChoiceOption } from "./choiceModel";
import { WorkflowOptionPicker } from "./WorkflowOptionPicker";
import { WorkflowChoiceResult } from "./WorkflowResultPresentation";

type Props = {
    config?: ChoiceEditorConfig | null;
    options: readonly WorkflowChoiceOption[];
    value: string | null;
    title: string;
    content: string;
    disabled: boolean;
    onChange: (selection: ChoicePreview) => void;
};
const backend = (import.meta.env.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

/** The choice and its regenerated explanation are one edit, applied only after review. */
export function WorkflowChoiceEditor({ config, options, value, title, content, disabled, onChange }: Props) {
    const { t } = useTranslation();
    const { instance } = useMsal();
    const [selection, setSelection] = useState<string | null>(null);
    const [preview, setPreview] = useState<ChoicePreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const request = useRef<AbortController | null>(null);
    const identity = JSON.stringify([config, value, content]);
    const blocked = disabled || !config;
    const close = () => {
        request.current?.abort();
        setSelection(null);
        setPreview(null);
        setError(null);
        setBusy(false);
    };
    useEffect(() => {
        close();
        return () => request.current?.abort();
    }, [identity, disabled]);
    const generate = async (next: string) => {
        if (blocked || !config || !options.some(option => option.value === next)) return;
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        setSelection(next);
        setPreview(null);
        setError(null);
        setBusy(true);
        try {
            const token = useMsalAuth ? await getToken(instance) : undefined;
            if (controller.signal.aborted) return;
            const parts = [config.project_id, config.draft_id, config.instance_id, config.node_id].map(encodeURIComponent);
            const response = await fetch(`${backend}/projects/${parts[0]}/documentation-drafts/${parts[1]}/boxes/${parts[2]}/${parts[3]}/choice-preview`, {
                method: "POST",
                signal: controller.signal,
                headers: { ...(await getHeaders(token)), "Content-Type": "application/json" },
                body: JSON.stringify({ value: next, expected_revision: config.expected_revision })
            });
            if (!response.ok) throw new Error(t("documentFlow.choice.failed"));
            const result = choicePreviewSchema.parse(await response.json());
            if (result.value !== next) throw new Error(t("documentFlow.choice.failed"));
            if (!controller.signal.aborted) setPreview(result);
        } catch (caught: unknown) {
            if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : t("documentFlow.choice.failed"));
        } finally {
            if (!controller.signal.aborted) setBusy(false);
        }
    };
    return (
        <WorkflowChoiceResult
            picker={<WorkflowOptionPicker options={options} value={value ?? ""} disabled={blocked} onSelect={next => void generate(next)}
                label={title} placeholder={t("documentFlow.choice.placeholder")} searchPlaceholder={t("documentFlow.choice.search")}
                emptyText={t("documentFlow.choice.empty")} />}
            content={content}
        >
            <Modal open={selection !== null} size="md" onOpenChange={open => { if (!open) close(); }}>
                <Modal.Header title={t("documentFlow.choice.review")} />
                <Modal.Body>
                    <p>{t("documentFlow.choice.notice")}</p>
                    <strong>{options.find(option => option.value === selection)?.label}</strong>
                    {busy && <p role="status">{t("documentFlow.choice.generating")}</p>}
                    {error && <p role="alert">{error}</p>}
                    {preview && <p style={{ whiteSpace: "pre-wrap" }}>{preview.content}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close}>{t("tooltips.cancel")}</Button>
                    {error && selection && <Button onClick={() => void generate(selection)}>{t("documentFlow.retry")}</Button>}
                    <Button disabled={blocked || busy || !preview} onClick={() => {
                        if (preview && !blocked) { onChange(preview); close(); }
                    }}>{t("documentFlow.choice.confirm")}</Button>
                </Modal.Footer>
            </Modal>
        </WorkflowChoiceResult>
    );
}
